import React, { useEffect, useState } from 'react';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import * as URX_FORM from '../constants/urx-form';
import { UT30CloudNative } from '../constants/spread';
import { Helmet } from 'react-helmet';
import {
  Button,
  ButtonSet,
  ModalWrapper,
  SkeletonPlaceholder,
  Tag,
  TextInput,
  Tooltip,
} from 'carbon-components-react';
import './QRadarCloudNativePriceEstimator.scss';
import 'src/common/style/common.scss';
import { usePrice } from '../hooks/usePrice';
import Cookies from 'js-cookie';
import Footnote from './Footnote';
import messages from '../locales/messages';
import { FormattedMessage } from 'react-intl';
import Thanks from './Thanks';
import { segmentsTracking } from 'src/common/lib/tracker';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import {
  LangDefaults,
  determineCountry,
  useLang,
} from 'src/common/hooks/widgets/useLang';
import {
  DEFAULT_INGESTION,
  MAX_INGESTION,
  MIN_INGESTION,
  Package,
} from '../constants/default';
import { useNumberFormat } from 'src/common/hooks/widgets/useNumberFormat';
import { FormModal } from 'src/common/utils/FormModal';
import countryList from '../services/currencies.json';
import DisclaimerText from 'src/common/components/DisclaimerText/DisclaimerText';

export interface QRadarCloudNativePriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const QRadarCloudNativePriceEstimator = ({
  langCode,
  test,
  countryToTest,
}: QRadarCloudNativePriceEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');

  // lang and userlocale is based out of ddo
  // Note - lang & userLocale to be used once widget is globalized which is in the pipeline
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  /* Have urx form deault in us-en when on language us page */
  const [urxlang] = useState(localeCode.toString());
  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);

  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);
  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);

  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const [environment] = useEnv();
  const [formComplete, setFormComplete] = useState(false);
  const [packageName] = useState(Package.essentials);
  const [showBuy] = useState(false);
  const cookieURL = useCookiesURL();
  const { bpName, logo, companyId, desc, mssp } = useBPName(
    UT30CloudNative,
    true,
  );

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  const [validations, IsValidations] = useState(false);
  const [ingestion, setIngestion] = useState(DEFAULT_INGESTION);
  const [formattedIngestion] = useNumberFormat(ingestion);

  const [totalCostPerGB, totalCostMonthly, loading] = usePrice({
    currency,
    ingestion,
    localeCode,
    country,
  });

  useEffect(() => {
    setCookie();
  }, [totalCostMonthly, totalCostPerGB, ingestion]);

  const getBp = () => {
    if (bpName) return bpName;
    if (logo) return companyId;
    if (desc) return desc;
    if (mssp) return mssp;

    const bpField = document.querySelector('#Q_BPNAME') as HTMLInputElement;
    return bpField ? bpField.value : '';
  };

  const setCookie = () => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            ' | Package: ' +
            packageName +
            ' | Daily log ingestion needed (GB/Day): ' +
            formattedIngestion +
            ' | Estimated Monthly cost: ' +
            totalCostMonthly +
            ' | Estimated Annual cost: ' +
            totalCostPerGB +
            (getBp() ? `|Preferred BP: ${getBp()}` : ''),
        },
        Q_ASSET_URL: window.location.href, // ISC - Asset URL initial
        Q_ASSET_NAME: 'IBM QRadar Cloud Native Estimator', // ISC - Asset Name
      }),
    );
    console.log(Cookies.get('urxdcq'));
  };

  const handleValidations = (
    value: number,
    IsValidations: React.Dispatch<React.SetStateAction<boolean>>,
    setIngestion: React.Dispatch<React.SetStateAction<number>>,
  ) => {
    if (value > MAX_INGESTION) {
      IsValidations(true);
      setIngestion(MAX_INGESTION);
    } else if (value < MIN_INGESTION) {
      IsValidations(true);
      setIngestion(value);
    } else {
      setIngestion(value);
    }
  };

  return (
    <div className="qradar-cloud-native widget-styles">
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          ingestion={ingestion}
          totalCostPerGB={`${totalCostPerGB}`}
          totalMonthlyCost={`${totalCostMonthly}`}
          bpName={bpName}
        />
      ) : (
        <div id="QRadarCloudNativeGrid" className="bx--grid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row">
            <div
              className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12"
              id="countryModal"
            >
              {/* Currently switched off - false to be removed once globalised */}
              {localExist && toggleCountry && false ? (
                <ModalWrapper
                  modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage} (${modalLocalCurrency}) `}
                  modalHeading={`The QRadar estimator is available in ${modalLocalLanguage} with local currency ${modalLocalCurrency}`}
                  size={'sm'}
                  primaryButtonText={'Cool! Take me there'}
                  secondaryButtonText={''}
                  buttonTriggerText={`Access page in ${modalLocalLanguage}`}
                  handleOpen={() => {
                    segmentsTracking('CTA Clicked', {
                      pageTitle: 'Cloud Native Pricing Page',
                      object: 'Pricing page',
                      resultValue: 'CTA clicked',
                      CTA: 'Access button clicked',
                      location: 'Ingestion Tooltip',
                      action: '',
                      field: '',
                    });
                  }}
                  handleSubmit={() => {
                    localStorage.setItem('langLocale', selectedLocale);
                    (window.location.href =
                      'https://www.ibm.com/' +
                      selectedLocale +
                      '/products/api-connect'),
                      segmentsTracking('CTA Clicked', {
                        pageTitle: 'Cloud Native Pricing Page',
                        object: 'Pricing page',
                        resultValue: 'CTA clicked',
                        CTA: 'Redirected to translated page',
                        location: 'Ingestion Tooltip',
                        action: '',
                        field: '',
                      });
                    return true;
                  }}
                ></ModalWrapper>
              ) : null}
            </div>
          </div>
          <div className="bx--row mar3">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
              <div className="bx--row">
                <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-4">
                  <div className="page_heading_3 marginleft_1">
                    <FormattedMessage {...messages.maas_saas_headline} />
                  </div>
                </div>
              </div>
              <p className="page_sub_heading marginleft_1 mar9">
                <FormattedMessage {...messages.maas_saas_sub_headline} />
              </p>
              <div className="bx--row mar9">
                <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16 mar2">
                  <div className="bx--row mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 marginleft_1">
                      <Tooltip
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'Cloud Native Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Essentials Tile',
                            location: 'Ingestion Tooltip',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'left' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            <FormattedMessage
                              {...messages.input_ingestion_text}
                            />
                          </span>
                        }
                      >
                        <span className="slide_text">
                          <FormattedMessage
                            {...messages.input_ingestion_tooltip}
                          />
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row mt-05 mar2">
                    <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-16 marginleft_1">
                      <TextInput
                        className="bx--text-input bx--text-input--lg"
                        id="ingestion"
                        invalidText={
                          <FormattedMessage
                            {...messages.input_ingestion_invalid}
                          />
                        }
                        hideLabel={true}
                        labelText="Ingestion"
                        invalid={validations}
                        onFocus={() => setIngestion(0)}
                        value={formattedIngestion}
                        onChange={(e) => {
                          const element = e.currentTarget as HTMLInputElement;
                          const value = element.value.replaceAll(',', '');
                          const regex = new RegExp('^[01-9,]*$');
                          const invalid =
                            !regex.test(value) && value.length > 0;
                          IsValidations(invalid);
                          if (!invalid) {
                            handleValidations(
                              Number.parseInt(value),
                              IsValidations,
                              setIngestion,
                            );
                          }
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'Cloud Native Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Ingestion Input',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div id="footnote" className="div-only-lg">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 marginleft_1">
                        <div>
                          <Footnote numNote={1} />
                        </div>
                        <div className="footnote_text">
                          <DisclaimerText
                            localeCode={localeCode}
                            product="QRadar SIEM (Cloud-Native)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--col-lg-2"></div>
                <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16 mt-06">
                  <div id="stickyNav" className="bx--row">
                    <div className="sticky-nav-table price_table_comp">
                      <table className="cds--data-table cds--data-table--sm">
                        <thead>
                          <tr>
                            <th scope="col">
                              <div className="price_table_comp__heading">
                                <FormattedMessage {...messages.col1_text} />
                              </div>
                            </th>
                            <th scope="col">
                              <div className="cds--table-header-label">
                                <div className="price_table_comp__heading">
                                  <FormattedMessage {...messages.col2_text} />
                                </div>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody aria-live="polite">
                          <tr>
                            <td>
                              {' '}
                              <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                <FormattedMessage {...messages.gb_per_day} />
                              </div>
                            </td>
                            <td>
                              <div className="price_table_comp__quantity padding_8">
                                {`${formattedIngestion}`}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div id="price_toggle_one">
                        <div>
                          <>
                            <p
                              id="widget.estimated-cost"
                              className="price_table_comp__bodycopy padding_24_2"
                            >
                              <FormattedMessage
                                {...messages.estimated_daily_text}
                              />
                              <sup className="qradar-cloud-native__sup">1</sup>
                            </p>

                            <div className="padding_8 price_table_comp__price">
                              {loading ? (
                                <SkeletonPlaceholder className="qradar-cloud-native__sticky-nav-skeleton" />
                              ) : (
                                `${totalCostPerGB}*`
                              )}
                            </div>
                            <p
                              id="widget.estimated-cost"
                              className="price_table_comp__bodycopy padding_24_2"
                            >
                              <FormattedMessage
                                {...messages.estimated_monthly_text}
                              />
                              <sup className="qradar-cloud-native__sup">1</sup>
                            </p>
                            <div className="padding_8 price_table_comp__price">
                              {loading ? (
                                <SkeletonPlaceholder className="qradar-cloud-native__sticky-nav-skeleton" />
                              ) : (
                                `${totalCostMonthly}*`
                              )}
                            </div>
                            <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                              * <FormattedMessage {...messages.prcie_text} />
                            </p>
                            <ButtonSet className="qradar-cloud-native__stack-button">
                              <Button
                                id="request_btn"
                                className={`${showBuy ? 'marright' : ''}`}
                                onClick={() => {
                                  segmentsTracking('CTA Clicked', {
                                    pageTitle: 'Cloud Native Pricing Page',
                                    object: 'Pricing page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Request a quote now',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  initUrxForm(
                                    environment,
                                    URX_FORM.INSTANCE_ID,
                                    URX_FORM.FORM_ID,
                                    urxlang as IBMLocale,
                                    companyId,
                                  );
                                  setDrawerOpen(true);
                                  setCookie();
                                }}
                              >
                                <FormattedMessage {...messages.quote_text} />
                              </Button>
                            </ButtonSet>
                          </>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div id="footnote" className="div-only-media">
                    <div className="bx--row mar9">
                      <div className="bx--col-lg-9">
                        <div>
                          <Footnote numNote={1} />
                        </div>
                        <DisclaimerText
                          localeCode={localeCode}
                          product="QRadar SIEM (Cloud-Native)"
                        />
                      </div>
                    </div>
                  </div>
                  <FormModal
                    pageTitle="Cloud Native Pricing Page"
                    object="Cloud Native Pricing Page"
                    productTitle={'Qradar Cloud Native'}
                    pageUrl={''}
                    location={'Estimator'}
                    bpName={bpName}
                    logo={logo}
                    drawerOpen={drawerOpen}
                    setDrawerOpen={setDrawerOpen}
                    formId={URX_FORM.FORM_ID}
                    lang={lang}
                  ></FormModal>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRadarCloudNativePriceEstimator;
